import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import { useNotificationContext, usePartnerContext } from '../../context'
import {
  ARCHIVED_CAREER_ACTIONS,
  ARCHIVED_CAREER_HINTS,
  ARCHIVED_CAREER_SKIPPED,
  partitionSpacesByArchivedCareer
} from '../../features/pricing/archived-careers'
import { mutation } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import {
  computeExpiresAt,
  getCommonExpiresAfterDays,
  isDeadlineRunning,
  pickerDateToExpiresAt,
  toPickerDate
} from '../../helpers/assessment-deadline'
import { useArchivedCareerIds } from '../../hooks/use-archived-career-ids'
import { useCreateLog } from '../../hooks/use-create-log'
import Datepicker from '../react-datepicker/datepicker'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'
import ArchivedCareerRestoreAction from './archived-career-restore-action'

// Geteilter Default statt einer neuen Map bei jedem Render
const NO_CAREERS = new Map()

const ResetExpired = ({
  spaces,
  careers_map = NO_CAREERS,
  updateHandler,
  open,
  setOpen
}) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()

  const space = spaces.length === 1 ? spaces[0] : null

  const { createSpaceHistoryLog } = useCreateLog()

  // D16, jeder Modus. Die Aktion ist bei archivierter Stelle nicht verfügbar
  // (use-space-actions). Wird der Dialog trotzdem damit geöffnet, werden diese
  // Talente übersprungen.
  const { archivedCareerIds } = useArchivedCareerIds({ spaces })
  const { allowed, skipped } = partitionSpacesByArchivedCareer(
    spaces,
    archivedCareerIds
  )
  const onlyArchived = !!skipped.length && !allowed.length

  const [isLoading, setIsLoading] = useState(false)

  // Vorschlag ist die laufende Frist eines einzelnen Talents, sonst heute plus
  // die Bearbeitungszeit der Stellen
  const expiresAfterDays = getCommonExpiresAfterDays(
    partner.app_settings,
    allowed.map(({ career_id }) => careers_map.get(career_id)?.app_settings)
  )
  const defaultDay = useMemo(
    () =>
      toPickerDate(
        space && isDeadlineRunning(space.expiresAt)
          ? space.expiresAt
          : computeExpiresAt(expiresAfterDays)
      ),
    [space, expiresAfterDays]
  )
  const [pickedDay, setPickedDay] = useState(null)

  // Die Komponente bleibt auf der Talents-Seite gemountet, deshalb bei jedem
  // Öffnen neu vom Vorschlag ausgehen
  useEffect(() => {
    if (open) setPickedDay(null)
  }, [open])

  const selectedDay = pickedDay || defaultDay

  const updateExpiresAt = async () => {
    if (onlyArchived) return

    setIsLoading(true)

    const event = { context: 'SYSTEM', status: 'RESET_EXPIRED' }

    const onSuccess = () => {
      success(
        [
          t('space_actions.reset_expired_success'),
          skipped.length &&
            t(ARCHIVED_CAREER_SKIPPED, { count: skipped.length })
        ]
          .filter(Boolean)
          .join(' ')
      )
      trackEvent(trackEvents.RESET_EXPIRED_TALENTS)
      setIsLoading(false)
      setOpen(false)
    }

    const expiresAt = pickerDateToExpiresAt(selectedDay)

    try {
      for (const space of allowed) {
        const { id: space_id, career_id } = space

        await createSpaceHistoryLog({ space_id, career_id, ...event })
        await mutation({
          mutation: 'updateSpace',
          input: { id: space_id, expiresAt }
        })
      }

      updateHandler({ onSuccess })
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='2xl'>
      <div className='mt-3 text-center sm:mt-5'>
        <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
          {t('space_actions.modal_reset_expired_title')}
        </h2>
        {spaces.length > 1 && (
          <p className='mx-auto mt-2 max-w-lg text-sm text-gray-700'>
            {t('space_actions.modal_reset_expired_description', {
              number: spaces.length
            })}
          </p>
        )}
      </div>
      {onlyArchived ? (
        <InfoCallout
          tone='warning'
          className='mx-4 mt-12'
          action={
            <ArchivedCareerRestoreAction onNavigate={() => setOpen(false)} />
          }
        >
          {t(ARCHIVED_CAREER_HINTS[ARCHIVED_CAREER_ACTIONS.RESET_EXPIRED])}
        </InfoCallout>
      ) : (
        <div className='mx-4 mt-12'>
          <p className='block text-sm font-medium text-gray-700'>
            {t('space_actions.reset_expired_title')}
          </p>
          <div className='mt-2'>
            <Datepicker
              selected={selectedDay}
              onChange={(date) => setPickedDay(date)}
              minDate={toPickerDate(new Date().toISOString())}
            />
          </div>
          <p className='mt-2 text-xs italic text-gray-500'>
            {t('space_actions.reset_expired_description')}
          </p>
        </div>
      )}

      <div className='mt-12 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 md:flex md:justify-center'>
        <Button.SecondaryXL
          text={t('space_actions.modal_reset_expired_cancel_action')}
          onClick={() => setOpen(false)}
        />
        <Button.PrimaryXL
          text={t('space_actions.modal_reset_expired_submit_action')}
          onClick={updateExpiresAt}
          isLoading={isLoading}
          disabled={onlyArchived}
        />
      </div>
    </Modal>
  )
}

ResetExpired.propTypes = {
  spaces: PropTypes.array.isRequired,
  // Stellen nach ID wie auf der Talents-Seite, für die Bearbeitungszeit
  careers_map: PropTypes.instanceOf(Map),
  updateHandler: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

export default ResetExpired
